import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";


dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const DATE_KEY_FORMAT = "YYYY-MM-DD";
const DEFAULT_TIME = "00:00";
const HAS_OFFSET_OR_Z = /(?:[zZ]|[+-]\d{2}:\d{2})$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

const toDateKey = (year: number, month: number, date: number) => (
  `${year}-${String(month + 1).padStart(2, "0")}-${String(date).padStart(2, "0")}`
);

const normalizeTime = (value?: string) => (
  value && TIME_PATTERN.test(value) ? value : DEFAULT_TIME
);

const toStorageIso = (dateKey: string, timezone: string, time = DEFAULT_TIME) => (
  dayjs.tz(`${dateKey}T${normalizeTime(time)}:00`, timezone).utc().toISOString()
);

const parseDateInTimezone = (
  value: Dayjs | string | undefined,
  timezone: string,
) => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    const parsedDate = dayjs(value).tz(timezone);
    return parsedDate.isValid() ? parsedDate : null;
  }

  const parsedDate = HAS_OFFSET_OR_Z.test(value)
    ? dayjs.utc(value).tz(timezone)
    : dayjs.tz(value, timezone);

  return parsedDate.isValid() ? parsedDate : null;
};

interface CalendarPopupProps {
  mode: "date" | "range";
  timezone?: string;
  withTimeSelection?: boolean;
  initialStartTime?: string;
  initialEndTime?: string;
  availableRange?: { start: Dayjs; end: Dayjs };
  defaultVisible?: Dayjs;
  anchorRef: React.RefObject<HTMLElement | null>;
  onSelect:
  | ((selected: string) => void)
  | ((start: string, end: string) => void);
}

const SM_BREAKPOINT = 640;
const FIXED_WIDTH_ABOVE_SM = 320;
const POPUP_OFFSET = 8;

const CalendarPopup = forwardRef<HTMLDivElement, CalendarPopupProps>(({
  mode,
  timezone,
  withTimeSelection = false,
  initialStartTime,
  initialEndTime,
  onSelect,
  availableRange,
  defaultVisible,
  anchorRef,
}, forwardedRef) => {
  const resolvedTimezone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const isAboveSm = window.innerWidth >= SM_BREAKPOINT;

      setPosition({
        top: rect.bottom + POPUP_OFFSET,
        left: rect.left,
        width: isAboveSm ? FIXED_WIDTH_ABOVE_SM : rect.width,
      });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorRef]);


  const initialVisibleDate = useMemo(
    () => (
      parseDateInTimezone(defaultVisible, resolvedTimezone) ||
      dayjs().tz(resolvedTimezone)
    ),
    [defaultVisible, resolvedTimezone],
  );

  const [visibleYear, setVisibleYear] = useState(initialVisibleDate.year());
  const [visibleMonth, setVisibleMonth] = useState(initialVisibleDate.month());
  const [selected, setSelected] = useState<string | undefined>();
  const [blankDays, setBlankDays] = useState<number[]>([]);
  const [noOfDays, setNoOfDays] = useState<number[]>([]);
  const [rangeStart, setRangeStart] = useState<string | undefined>();
  const [rangeEnd, setRangeEnd] = useState<string | undefined>();
  const [rangeStartTime, setRangeStartTime] = useState(
    normalizeTime(initialStartTime),
  );
  const [rangeEndTime, setRangeEndTime] = useState(
    normalizeTime(initialEndTime),
  );
  const lastEmittedRangeRef = useRef<{ start: string; end: string } | null>(null);
  const lastEmittedDateRef = useRef<string | null>(null);

  const availableRangeKeys = useMemo(() => {
    if (!availableRange?.start || !availableRange?.end) {
      return null;
    }

    const startKey = parseDateInTimezone(
      availableRange.start,
      resolvedTimezone,
    )?.format(DATE_KEY_FORMAT);
    const endKey = parseDateInTimezone(
      availableRange.end,
      resolvedTimezone,
    )?.format(DATE_KEY_FORMAT);

    if (!startKey || !endKey) {
      return null;
    }

    return startKey <= endKey
      ? { start: startKey, end: endKey }
      : { start: endKey, end: startKey };
  }, [availableRange, resolvedTimezone]);

  const WEEKDAYS = dayjs.weekdaysShort();
  const MONTH_NAMES = dayjs.months();

  useEffect(() => {
    setVisibleYear(initialVisibleDate.year());
    setVisibleMonth(initialVisibleDate.month());
  }, [initialVisibleDate]);

  useEffect(() => {
    setRangeStartTime(normalizeTime(initialStartTime));
    setRangeEndTime(normalizeTime(initialEndTime));
  }, [initialEndTime, initialStartTime]);

  useEffect(() => {
    if (mode === "range") {
      if (rangeStart && rangeEnd) {
        const isOrdered = rangeStart <= rangeEnd;
        const [startKey, endKey] = isOrdered
          ? [rangeStart, rangeEnd]
          : [rangeEnd, rangeStart];
        const [startTime, endTime] = isOrdered
          ? [rangeStartTime, rangeEndTime]
          : [rangeEndTime, rangeStartTime];
        const emittedStart = toStorageIso(
          startKey,
          resolvedTimezone,
          withTimeSelection ? startTime : DEFAULT_TIME,
        );
        const emittedEnd = toStorageIso(
          endKey,
          resolvedTimezone,
          withTimeSelection ? endTime : DEFAULT_TIME,
        );

        if (
          lastEmittedRangeRef.current?.start === emittedStart &&
          lastEmittedRangeRef.current?.end === emittedEnd
        ) {
          return;
        }

        lastEmittedRangeRef.current = { start: emittedStart, end: emittedEnd };
        lastEmittedDateRef.current = null;

        (onSelect as (start: string, end: string) => void)(
          emittedStart,
          emittedEnd,
        );
      }

      return;
    }

    if (selected) {
      const emittedDate = toStorageIso(selected, resolvedTimezone);

      if (lastEmittedDateRef.current === emittedDate) {
        return;
      }

      lastEmittedDateRef.current = emittedDate;
      lastEmittedRangeRef.current = null;

      (onSelect as (selected: string) => void)(
        emittedDate,
      );
    }
  }, [
    mode,
    onSelect,
    rangeStart,
    rangeEnd,
    rangeStartTime,
    rangeEndTime,
    resolvedTimezone,
    selected,
    withTimeSelection,
  ]);

  useEffect(() => {
    getNoOfDays();
  }, [visibleMonth, visibleYear]);

  useEffect(() => {
    if (noOfDays?.length > 0) {
      // removed animation
    }
  }, [noOfDays]);

  const previousMonth = () => {
    if (visibleMonth == 0) {
      setVisibleYear(visibleYear - 1);
      setVisibleMonth(11);
    } else {
      setVisibleMonth(visibleMonth - 1);
    }
  };

  const nextMonth = () => {
    if (visibleMonth == 11) {
      setVisibleYear(visibleYear + 1);
      setVisibleMonth(0);
    } else {
      setVisibleMonth(visibleMonth + 1);
    }
  };

  const isSelectedDate = (date: number) => {
    const dateKey = toDateKey(visibleYear, visibleMonth, date);

    if (mode === "range" && rangeStart && rangeEnd) {
      const [startKey, endKey] =
        rangeStart <= rangeEnd
          ? [rangeStart, rangeEnd]
          : [rangeEnd, rangeStart];

      return dateKey >= startKey && dateKey <= endKey;
    }

    return selected === dateKey;
  };

  const isToday = (date: number) => {
    const today = dayjs().tz(resolvedTimezone).format(DATE_KEY_FORMAT);
    const dateKey = toDateKey(visibleYear, visibleMonth, date);

    return today === dateKey;
  };

  const getDateValue = (date: number) => {
    const selectedDateKey = toDateKey(visibleYear, visibleMonth, date);

    if (mode === "range") {
      if (!rangeStart && !rangeEnd) {
        setSelected(selectedDateKey);
        setRangeStart(selectedDateKey);
      } else if (!rangeEnd) {
        setRangeEnd(selectedDateKey);
        setSelected(undefined);
      } else {
        setSelected(selectedDateKey);
        setRangeStart(selectedDateKey);
        setRangeEnd(undefined);
      }
    } else {
      setSelected(selectedDateKey);
    }
  };

  const getNoOfDays = () => {
    const daysInMonth = new Date(visibleYear, visibleMonth + 1, 0).getDate();
    // find where to start calendar day of week
    const dayOfWeek = new Date(visibleYear, visibleMonth).getDay();

    const blankdaysArray = [];
    for (let i = 1; i <= dayOfWeek; i++) {
      blankdaysArray.push(i);
    }
    const daysArray = [];
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }

    setBlankDays(blankdaysArray);
    setNoOfDays(daysArray);
  };

  const isAvailable = (date: number) => {
    if (!availableRangeKeys) {
      return true;
    }

    const dateKey = toDateKey(visibleYear, visibleMonth, date);

    return dateKey >= availableRangeKeys.start && dateKey <= availableRangeKeys.end;
  };

  if (!position || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      ref={forwardedRef}
      role="dialog"
      aria-label="Calendar"
      className={classNames(
        "fixed z-50 origin-top rounded-lg border-2 shadow-lg backdrop-blur-sm",
        "border-primary-200 dark:border-primary-800",
        "bg-white/95 dark:bg-primary-900/95",
      )}
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b p-3 dark:border-primary-800/50">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-primary-700 dark:text-primary-300">
            {MONTH_NAMES[visibleMonth]}
          </span>
          <span className="text-sm text-primary-500 dark:text-primary-500">
            {visibleYear}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={previousMonth}
            className={classNames(
              "rounded-md p-1.5 transition-colors cursor-pointer",
              "hover:bg-primary-100 dark:hover:bg-primary-800",
              "text-primary-500 dark:text-primary-400"
            )}
            aria-label="Previous month"
          >
            <div className="icon-[ph--caret-left] h-4 w-4" />
          </button>
          <button
            onClick={nextMonth}
            className={classNames(
              "rounded-md p-1.5 transition-colors cursor-pointer",
              "hover:bg-primary-100 dark:hover:bg-primary-800",
              "text-primary-500 dark:text-primary-400"
            )}
            aria-label="Next month"
          >
            <div className="icon-[ph--caret-right] h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-3">
        {/* Weekday Headers */}
        <div className="mb-2 grid grid-cols-7 gap-1">
          {WEEKDAYS.map((day: string) => (
            <div
              key={day}
              className="day-label text-center text-xs font-medium text-primary-400 dark:text-primary-600"
            >
              {day.slice(0, 2)}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-1">
          {blankDays.map((blankday) => (
            <div
              key={blankday}
              className="aspect-square border border-transparent"
            />
          ))}

          {noOfDays.map((date, dateIndex) => {
            const isDateAvailable = isAvailable(date);
            const isDateSelected = isSelectedDate(date);
            const isDateToday = isToday(date);
            const dateKey = toDateKey(visibleYear, visibleMonth, date);

            return (
              <div
                key={dateIndex}
                className={classNames(
                  "day relative aspect-square",
                  !isDateAvailable && "pointer-events-none opacity-30"
                )}
              >
                <button
                  onClick={() => getDateValue(date)}
                  disabled={!isDateAvailable}
                  aria-label={dateKey}
                  className={classNames(
                    "group flex h-full w-full items-center justify-center rounded-md text-sm transition-colors",
                    isDateAvailable && "cursor-pointer",
                    isDateSelected && "bg-primary-500 text-white dark:bg-primary-400",
                    !isDateSelected && isDateAvailable && "hover:bg-primary-100 dark:hover:bg-primary-800",
                    isDateToday && !isDateSelected && "text-primary-500 dark:text-primary-400 font-medium",
                    !isDateSelected && !isDateToday && "text-primary-700 dark:text-primary-300"
                  )}
                >
                  <span className="relative z-10">{date}</span>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {mode === "range" && withTimeSelection && (
        <div className="border-t border-primary-200/70 p-3 dark:border-primary-800/70">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2">
            <div className="w-full flex flex-col items-center">
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-primary-500 dark:text-primary-500">
                Start
              </label>
              <input
                type="time"
                step={60}
                disabled={!rangeStart}
                value={rangeStartTime}
                onChange={(event) => setRangeStartTime(normalizeTime(event.target.value))}
                className={classNames(
                  "h-8 w-full min-w-0 rounded-md border bg-white px-1.5 text-[11px] font-medium outline-none transition-colors dark:bg-primary-900 !sm:w-[112px]",
                  !rangeStart
                    ? "cursor-not-allowed border-primary-200 text-primary-400 dark:border-primary-800 dark:text-primary-600"
                    : "border-primary-300 text-primary-700 hover:border-primary-400 focus:border-primary-500 dark:border-primary-700 dark:text-primary-300 dark:hover:border-primary-600 dark:focus:border-primary-500",
                )}
              />
            </div>

            <div className="w-full flex flex-col items-center">
              <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-primary-500 dark:text-primary-500">
                End
              </label>
              <input
                type="time"
                step={60}
                disabled={!rangeEnd}
                value={rangeEndTime}
                onChange={(event) => setRangeEndTime(normalizeTime(event.target.value))}
                className={classNames(
                  "h-8 w-full min-w-0 rounded-md border bg-white px-1.5 text-[11px] font-medium outline-none transition-colors dark:bg-primary-900 sm:w-[112px]",
                  !rangeEnd
                    ? "cursor-not-allowed border-primary-200 text-primary-400 dark:border-primary-800 dark:text-primary-600"
                    : "border-primary-300 text-primary-700 hover:border-primary-400 focus:border-primary-500 dark:border-primary-700 dark:text-primary-300 dark:hover:border-primary-600 dark:focus:border-primary-500",
                )}
              />
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
});

CalendarPopup.displayName = "CalendarPopup";

export default CalendarPopup;
